/**
 * Copyright (c) 2021 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	robin herrmann - itemis AG
 *
 */

import { shapes, dia } from "@clientio/rappid";
import { Transition } from "../transition";
import { Validation } from "./validation";
import { difference, removeStaticAttributes } from "./utils";
import { PreferenceStore } from "../../services/preference-store";
import { WarningsList } from "../WarningsList";
import { ErrorsList } from "../ErrorsList";
export class BaseNode extends dia.Element {
  errors: string[] = [];
  warnings: string[] = [];
  hasErrors() {
    return this.errors.length > 0;
  }
  getErrorText() {
    return this.errors.join("<br>");
  }
  hasWarnings() {
    return this.warnings.length > 0;
  }
  getWarningText() {
    return this.warnings.join("<br>");
  }

  getOutgoingTransitions(): Array<Transition> {
    return this.graph
      ?.getConnectedLinks(this, { outbound: true })
      .filter((l) => l instanceof Transition)
      .map((l) => l as Transition)
      .sort((a, b) => {
        if (a.index() === 0) return 1;
        if (b.index() === 0) return -1;
        return a.index() - b.index();
      });
  }

  updateTransitionPriorities() {
    this.getOutgoingTransitions().forEach((t, i) => {
      t.index(i + 1);
    });
  }

  toJSON() {
    const result = super.toJSON();
    result.attrs = difference(result.attrs, this.defaults().attrs);
    return removeStaticAttributes(result);
  }
}

export class BaseView extends dia.ElementView implements Validation {
  getModel() {
    return this.model as BaseNode;
  }

  getMinSize(direction: dia.Direction): { width: number; height: number } {
    return { width: 15, height: 15 };
  }

  displayDiagnostics() {
    ErrorsList.remove(this);
    WarningsList.remove(this);

    if (this.model.attr("root/display") === "none") {
      // skip inactive views
      return;
    }
    if (this.getModel().hasErrors()) {
      ErrorsList.add(this, "root", "errors-list", {
        attribute: "marker",
        size: { width: this.model.getBBox().width, height: 0 },
        opactity: 1,
        gap: 0,
        layer: dia.Paper.Layers.FRONT,
      });

      this.model.set("marker", [this.getModel().getErrorText()]);
    } else if (this.getModel().hasWarnings()) {
      WarningsList.add(this, "root", "warnings-list", {
        attribute: "marker",
        size: { width: this.model.getBBox().width, height: 0 },
        opactity: 1,
        gap: 0,
        layer: dia.Paper.Layers.FRONT,
      });
      this.model.set("marker", [this.getModel().getWarningText()]);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  highlight(el?: SVGElement | string, opt?: { [key: string]: any }) {
    if (opt["embedding"] || opt["connecting"]) {
      this.disableHover();
      return super.highlight(el, {
        ...opt,
        highlighter: {
          name: "addClass",
          options: {
            className: PreferenceStore.INSTANCE.theme.isDark ? "highlight-lighten" : "highlight-darken",
          },
        },
      });
    }
    return super.highlight(el, opt);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unhighlight(el?: SVGElement | string, opt?: { [key: string]: any }) {
    if (opt["embedding"] || opt["connecting"]) {
      this.enableHover();
      return super.unhighlight(el, {
        ...opt,
        highlighter: {
          name: "addClass",
          options: {
            className: PreferenceStore.INSTANCE.theme.isDark ? "highlight-lighten" : "highlight-darken",
          },
        },
      });
    }
    return super.unhighlight(el, opt);
  }

  enableHover() {
    if (this.el.classList.contains("hover-disabled")) {
      this.el.classList.remove("hover-disabled");
      const editorElements = document.querySelectorAll(".ace_editor");
      editorElements.forEach((editorElement) => {
        editorElement.classList.remove("hover-disabled");
      });
    }
  }

  disableHover() {
    if (!this.el.classList.contains("hover-disabled")) {
      this.el.classList.add("hover-disabled");
      const editorElements = document.querySelectorAll(".ace_editor");
      editorElements.forEach((editorElement) => {
        editorElement.classList.add("hover-disabled");
      });
    }
  }
}
